import { TeamListPage } from '@numeo/types-main-web-app'
import { compareByName } from './teamQuery'

/**
 * How one cached directory page takes a `team.peopleChanged` change made by someone else. Pure, and mutating the page it
 * is given (an Immer draft in `updateQueryData`), so the cache entry keeps its request time: a patch never restarts the
 * five-minute lifetime.
 */

export type RowChange<T> = { op: 'upsert'; id: string; row: T } | { op: 'remove'; id: string }

interface PatchContext<T> {
    /** Whether a row belongs in this page's query (its filters and search, evaluated on the row). */
    matches: (row: T) => boolean
    /** Whether the id is already on some cached page or named in the people cache: then it is not new to the company. */
    isKnown: (id: string) => boolean
    /** The page's order, as its query asked the server for it. Absent: by name. */
    compare?: (a: T, b: T) => number
    /** The wider-tier fields (manage, pay) of the cached row, which the base row a change carries never has. Absent: none. */
    widerTier?: (cached: T, row: T) => Partial<T>
}

/** The page holds every row its query admits (page 1, and nothing beyond it): an id absent from it is absent from the list. */
const isComplete = <T>(page: TeamListPage<T>): boolean => page.pagination.page === 1 && page.items.length >= page.pagination.total

type Compare<T> = (a: T, b: T) => number

/** Put `row` in the page's order; a page over its limit drops its last row (the next page starts with it). */
const insertSorted = <T extends { id: string; name?: string }>(page: TeamListPage<T>, row: T, compare: Compare<T>): void => {
    const at = page.items.findIndex((item) => compare(row, item) < 0)
    page.items.splice(at === -1 ? page.items.length : at, 0, row)
    if (page.items.length > page.pagination.limit) page.items.pop()
}

const sortsIntoWindow = <T extends { id: string; name?: string }>(page: TeamListPage<T>, row: T, compare: Compare<T>): boolean => {
    const last = page.items[page.items.length - 1]
    return page.items.length < page.pagination.limit || !last || compare(row, last) < 0
}

const applyUpsert = <T extends { id: string; name?: string }>(page: TeamListPage<T>, id: string, row: T, context: PatchContext<T>): void => {
    const compare = context.compare ?? compareByName
    const index = page.items.findIndex((item) => item.id === id)
    if (index >= 0) {
        // The base row replaces the cached one, so a field it leaves out (a truck taken away, a termination undone) is
        // cleared; only the wider-tier fields this page was served and the base row never carries are kept.
        const merged: T = { ...row, ...context.widerTier?.(page.items[index], row) }
        page.items.splice(index, 1)
        if (context.matches(merged)) insertSorted(page, merged, compare)
        else page.pagination.total = Math.max(0, page.pagination.total - 1)
        return
    }
    if (!context.matches(row)) return
    if (isComplete(page)) {
        insertSorted(page, row, compare)
        page.pagination.total += 1
        return
    }
    // A page that does not hold its whole list cannot tell a newcomer from someone on another page, unless nobody has
    // seen the id: then it is new, counted, and shown when it sorts onto page 1.
    if (context.isKnown(id)) return
    page.pagination.total += 1
    if (page.pagination.page === 1 && sortsIntoWindow(page, row, compare)) insertSorted(page, row, compare)
}

/** Apply the changes, in order, to one cached page. */
export const patchTeamPage = <T extends { id: string; name?: string }>(
    page: TeamListPage<T>,
    changes: readonly RowChange<T>[],
    context: PatchContext<T>
): void => {
    for (const change of changes) {
        if (change.op === 'upsert') {
            applyUpsert(page, change.id, change.row, context)
            continue
        }
        const index = page.items.findIndex((item) => item.id === change.id)
        if (index < 0) continue
        page.items.splice(index, 1)
        page.pagination.total = Math.max(0, page.pagination.total - 1)
    }
}
